/**
 * Quick View Modal
 * Lightweight product preview modal for collection pages
 * 
 * Features:
 * - AJAX product loading
 * - Variant selection
 * - Add to cart
 * - Image gallery with thumbnails
 * - Keyboard accessible (Escape to close, focus trap)
 * - Reduced motion support
 */

if (!customElements.get('quick-view-modal')) {
  class QuickViewModal extends HTMLElement {
    constructor() {
      super();
      this.modal = this;
      this.container = this.querySelector('.quick-view-modal__container');
      this.contentArea = this.querySelector('.quick-view-modal__content');
      this.closeBtn = this.querySelector('.quick-view-modal__close');
      this.currentProductUrl = null;
      this.currentVariant = null;
      this.productData = null;
      this.focusedElementBeforeOpen = null;
      
      this.bindEvents();
    }

    bindEvents() {
      // Close button
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      // Click outside to close
      this.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Escape key to close
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) {
          this.close();
        }
      });

      // Listen for quick-view trigger events
      document.addEventListener('quick-view:open', (e) => {
        if (e.detail && e.detail.productUrl) {
          this.open(e.detail.productUrl);
        }
      });
    }

    async open(productUrl) {
      this.focusedElementBeforeOpen = document.activeElement;
      this.currentProductUrl = productUrl;
      
      // Show modal with loading state
      this.showLoading();
      this.classList.add('is-open');
      document.body.classList.add('quick-view-open');
      
      try {
        // Fetch product data
        const response = await fetch(`${productUrl}.js`);
        this.productData = await response.json();
        
        // Fetch section HTML
        const sectionResponse = await fetch(`${productUrl}?view=quick-view`);
        
        if (sectionResponse.ok) {
          const html = await sectionResponse.text();
          this.renderContent(html);
        } else {
          // Fallback: render from JSON data
          this.renderFromJson(this.productData);
        }
        
        // Set up variant handling
        this.setupVariants();
        this.setupQuantity();
        this.setupAddToCart();
        this.setupGallery();
        
        // Focus management
        this.trapFocus();
        
      } catch (error) {
        console.error('Quick view error:', error);
        this.showError('Could not load product. Please try again.');
      }
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('quick-view-open');
      
      // Restore focus
      if (this.focusedElementBeforeOpen) {
        this.focusedElementBeforeOpen.focus();
      }
      
      // Clear content after animation
      setTimeout(() => {
        this.contentArea.innerHTML = '';
        this.currentProductUrl = null;
        this.productData = null;
      }, 300);
    }

    showLoading() {
      this.contentArea.innerHTML = `
        <div class="quick-view-modal__loading">
          <div class="quick-view-modal__spinner" aria-label="Loading..."></div>
        </div>
      `;
    }

    showError(message) {
      this.contentArea.innerHTML = `
        <div class="quick-view-modal__loading">
          <p style="text-align: center; opacity: 0.7;">${message}</p>
        </div>
      `;
    }

    renderContent(html) {
      // Parse the HTML response and extract quick-view content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const quickViewContent = doc.querySelector('.quick-view-content');
      
      if (quickViewContent) {
        this.contentArea.innerHTML = quickViewContent.innerHTML;
      } else {
        // Fallback to JSON rendering
        this.renderFromJson(this.productData);
      }
    }

    renderFromJson(product) {
      const variant = product.variants[0];
      this.currentVariant = variant;
      
      const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
      const savings = hasComparePrice ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : 0;
      
      // Build variant options HTML
      let variantsHtml = '';
      if (product.options && product.options.length > 0 && !(product.options.length === 1 && product.options[0].name === 'Title')) {
        variantsHtml = '<div class="quick-view-modal__variants">';
        product.options.forEach((option, optionIndex) => {
          variantsHtml += `
            <div class="quick-view-modal__variant-group">
              <label class="quick-view-modal__variant-label">${option.name}</label>
              <div class="quick-view-modal__variant-options" data-option-index="${optionIndex}">
                ${option.values.map((value, valueIndex) => `
                  <button 
                    type="button" 
                    class="quick-view-modal__variant-option${valueIndex === 0 ? ' is-selected' : ''}"
                    data-value="${value}"
                    data-option-index="${optionIndex}"
                  >
                    ${value}
                  </button>
                `).join('')}
              </div>
            </div>
          `;
        });
        variantsHtml += '</div>';
      }
      
      // Build thumbnails HTML
      let thumbnailsHtml = '';
      if (product.images && product.images.length > 1) {
        thumbnailsHtml = `
          <div class="quick-view-modal__thumbnails">
            ${product.images.map((img, i) => `
              <button 
                type="button" 
                class="quick-view-modal__thumbnail${i === 0 ? ' is-active' : ''}"
                data-image-index="${i}"
                aria-label="View image ${i + 1}"
              >
                <img src="${img.replace(/\.(jpg|jpeg|png|gif|webp)/, '_100x.$1')}" alt="" loading="lazy">
              </button>
            `).join('')}
          </div>
        `;
      }
      
      this.contentArea.innerHTML = `
        <div class="quick-view-modal__media">
          <img 
            class="quick-view-modal__image" 
            src="${product.featured_image || product.images[0]}" 
            alt="${product.title}"
            data-main-image
          >
          ${thumbnailsHtml}
        </div>
        
        <div class="quick-view-modal__info">
          ${product.vendor ? `<p class="quick-view-modal__vendor">${product.vendor}</p>` : ''}
          
          <h2 class="quick-view-modal__title">
            <a href="${this.currentProductUrl}">${product.title}</a>
          </h2>
          
          <div class="quick-view-modal__price">
            <span class="quick-view-modal__price-current" data-price>
              ${this.formatMoney(variant.price)}
            </span>
            ${hasComparePrice ? `
              <span class="quick-view-modal__price-compare" data-compare-price>
                ${this.formatMoney(variant.compare_at_price)}
              </span>
              <span class="quick-view-modal__price-badge">Save ${savings}%</span>
            ` : ''}
          </div>
          
          ${variantsHtml}
          
          <div class="quick-view-modal__quantity">
            <span class="quick-view-modal__quantity-label">Quantity</span>
            <div class="quick-view-modal__quantity-selector">
              <button type="button" class="quick-view-modal__quantity-btn" data-action="decrease" aria-label="Decrease quantity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              <input 
                type="number" 
                class="quick-view-modal__quantity-input" 
                value="1" 
                min="1" 
                max="99"
                aria-label="Quantity"
                data-quantity-input
              >
              <button type="button" class="quick-view-modal__quantity-btn" data-action="increase" aria-label="Increase quantity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
          </div>
          
          <form class="quick-view-modal__form">
            <input type="hidden" name="id" value="${variant.id}" data-variant-id>
            <input type="hidden" name="quantity" value="1" data-quantity-hidden>
            
            <button 
              type="submit" 
              class="quick-view-modal__add-btn"
              ${!variant.available ? 'disabled' : ''}
            >
              ${variant.available ? 'Add to Cart' : 'Sold Out'}
            </button>
          </form>
          
          <a href="${this.currentProductUrl}" class="quick-view-modal__view-full">
            View Full Details
          </a>
          
          ${product.description ? `
            <div class="quick-view-modal__description">
              <div class="quick-view-modal__description-text">
                ${product.description.substring(0, 300)}${product.description.length > 300 ? '...' : ''}
              </div>
            </div>
          ` : ''}
        </div>
      `;
    }

    setupVariants() {
      const optionButtons = this.querySelectorAll('.quick-view-modal__variant-option');
      
      optionButtons.forEach(button => {
        button.addEventListener('click', () => {
          const optionIndex = parseInt(button.dataset.optionIndex);
          const value = button.dataset.value;
          
          // Update selected state
          const siblings = button.parentElement.querySelectorAll('.quick-view-modal__variant-option');
          siblings.forEach(s => s.classList.remove('is-selected'));
          button.classList.add('is-selected');
          
          // Find matching variant
          this.updateVariant();
        });
      });
    }

    updateVariant() {
      if (!this.productData) return;
      
      // Get selected options
      const selectedOptions = [];
      const optionGroups = this.querySelectorAll('.quick-view-modal__variant-options');
      
      optionGroups.forEach(group => {
        const selected = group.querySelector('.is-selected');
        if (selected) {
          selectedOptions.push(selected.dataset.value);
        }
      });
      
      // Find matching variant
      const variant = this.productData.variants.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });
      
      if (variant) {
        this.currentVariant = variant;
        
        // Update variant ID
        const variantInput = this.querySelector('[data-variant-id]');
        if (variantInput) variantInput.value = variant.id;
        
        // Update price
        const priceEl = this.querySelector('[data-price]');
        if (priceEl) priceEl.textContent = this.formatMoney(variant.price);
        
        // Update compare price
        const comparePriceEl = this.querySelector('[data-compare-price]');
        if (comparePriceEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
            comparePriceEl.style.display = '';
          } else {
            comparePriceEl.style.display = 'none';
          }
        }
        
        // Update button state
        const addBtn = this.querySelector('.quick-view-modal__add-btn');
        if (addBtn) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
        }
        
        // Update image if variant has one
        if (variant.featured_image) {
          const mainImage = this.querySelector('[data-main-image]');
          if (mainImage) {
            mainImage.src = variant.featured_image.src;
          }
        }
      }
    }

    setupQuantity() {
      const decreaseBtn = this.querySelector('[data-action="decrease"]');
      const increaseBtn = this.querySelector('[data-action="increase"]');
      const input = this.querySelector('[data-quantity-input]');
      const hiddenInput = this.querySelector('[data-quantity-hidden]');
      
      if (!input) return;
      
      const updateQuantity = (newValue) => {
        const value = Math.max(1, Math.min(99, newValue));
        input.value = value;
        if (hiddenInput) hiddenInput.value = value;
      };
      
      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(input.value) - 1);
        });
      }
      
      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(input.value) + 1);
        });
      }
      
      input.addEventListener('change', () => {
        updateQuantity(parseInt(input.value) || 1);
      });
    }

    setupAddToCart() {
      const form = this.querySelector('.quick-view-modal__form');
      if (!form) return;
      
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const addBtn = this.querySelector('.quick-view-modal__add-btn');
        if (!addBtn || addBtn.disabled) return;
        
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
        
        try {
          const formData = new FormData(form);
          
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (response.ok) {
            // Success - close modal and open cart
            this.close();
            
            // Trigger cart update
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            
            // Try to open cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              setTimeout(() => cartDrawer.open(), 100);
            }
            
            // Update cart count
            this.updateCartCount();
          } else {
            const error = await response.json();
            alert(error.description || 'Could not add to cart');
            addBtn.classList.remove('is-loading');
            addBtn.disabled = false;
          }
        } catch (error) {
          console.error('Add to cart error:', error);
          alert('Something went wrong. Please try again.');
          addBtn.classList.remove('is-loading');
          addBtn.disabled = false;
        }
      });
    }

    setupGallery() {
      const thumbnails = this.querySelectorAll('.quick-view-modal__thumbnail');
      const mainImage = this.querySelector('[data-main-image]');
      
      if (!mainImage || !this.productData) return;
      
      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.dataset.imageIndex);
          const image = this.productData.images[index];
          
          if (image) {
            mainImage.src = image;
            
            // Update active state
            thumbnails.forEach(t => t.classList.remove('is-active'));
            thumb.classList.add('is-active');
          }
        });
      });
    }

    trapFocus() {
      const focusableElements = this.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return;
      
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];
      
      // Focus first element
      setTimeout(() => firstFocusable.focus(), 100);
      
      // Trap focus
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      });
    }

    async updateCartCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        
        const countElements = document.querySelectorAll('.cart-count-bubble span:not(.visually-hidden)');
        countElements.forEach(el => {
          el.textContent = cart.item_count;
        });
        
        const bubbles = document.querySelectorAll('.cart-count-bubble');
        bubbles.forEach(bubble => {
          bubble.classList.toggle('hidden', cart.item_count === 0);
        });
      } catch (error) {
        console.error('Failed to update cart count:', error);
      }
    }

    formatMoney(cents) {
      // Simple formatting - use Shopify's format in production
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('quick-view-modal', QuickViewModal);
}

// Quick view trigger helper
document.addEventListener('DOMContentLoaded', () => {
  // Add click handlers to quick view buttons
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-quick-view-trigger]');
    if (trigger) {
      e.preventDefault();
      const productUrl = trigger.dataset.productUrl || trigger.href;
      
      if (productUrl) {
        document.dispatchEvent(new CustomEvent('quick-view:open', {
          detail: { productUrl }
        }));
      }
    }
  });
});
